import React, { useState, useMemo } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Chip,
  Select,
  MenuItem,
  Input,
  SelectChangeEvent,
  SxProps,
  ListSubheader,
  TextField,
  IconButton,
} from "@mui/material";

import { formatColumnCaption } from "../../utils/mui_table_utils";

const CustomIcon = ({
  toggleOpen,
  open,
}: {
  toggleOpen: (e: React.MouseEvent) => void;
  open: boolean;
}) => (
  <Box
    onClick={toggleOpen}
    sx={{
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "30px",
      height: "30px",
    }}
  >
    {open ? (
      <ArrowDropUpIcon fontSize="medium" sx={{ color: "action.active" }} />
    ) : (
      <ArrowDropDownIcon fontSize="medium" sx={{ color: "action.active" }} />
    )}
  </Box>
);

const containsText = (text: string, searchText: string) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

export function MultiSelect({
  isLoading = false,
  placeholder,
  selectedOptions,
  handleChange,
  handleDelete,
  options,
  sx,
  searchable = false,
  noFormatting = false,
  disabledDeleteValue,
}: {
  isLoading?: boolean;
  placeholder?: string;
  selectedOptions: string[];
  handleChange: (event: SelectChangeEvent<string[]>) => void;
  handleDelete: (option: string) => void;
  options: string[];
  sx?: SxProps;
  searchable?: boolean;
  noFormatting?: boolean;
  disabledDeleteValue?: string;
}) {
  const [open, setOpen] = useState(false);

  const toggleOpen = (e: React.MouseEvent) => {
    setOpen((prev) => !prev);
  };

  const [searchText, setSearchText] = useState("");

  const displayedOptions = useMemo(
    () =>
      options
        .filter((option) => containsText(option, searchText))
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
    [searchText, options]
  );

  return (
    <Select
      sx={{
        ...sx,
      }}
      disabled={isLoading}
      variant="standard"
      size="small"
      multiple
      // Render the placeholder when nothing is selected (so the filter shows its
      // name, like the SearchableDropdown filters). Gated on `placeholder` so
      // callers that don't pass one keep their exact prior empty-state behaviour.
      displayEmpty={!!placeholder}
      value={selectedOptions}
      onChange={handleChange}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      IconComponent={(props) => (
        <CustomIcon toggleOpen={toggleOpen} open={open} />
      )}
      input={
        <Input
          onClick={(e: React.MouseEvent<HTMLInputElement>) => {
            // Don't open if clicking on a delete button or chip
            const target = e.target as HTMLElement;
            if (
              target.closest("button") ||
              target.closest(".MuiChip-deleteIcon") ||
              target.closest(".MuiChip-root")
            ) {
              // Don't stop propagation - let Chip's onDelete handle it
              return;
            }
          }}
        />
      }
      renderValue={(selected) => (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            minWidth: "100%",
            pr: 2.75,
          }}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement;

            // Check if clicking on delete button
            const deleteButton =
              target.closest("button[aria-label*='Delete']") ||
              target.closest(".MuiChip-deleteIcon") ||
              (target.tagName === "BUTTON" && target.closest(".MuiChip-root"));

            if (deleteButton) {
              e.stopPropagation();
              e.preventDefault();
              // Find the chip and get its value
              const chip = target.closest(".MuiChip-root");
              if (chip) {
                const chipLabel = chip.querySelector(".MuiChip-label");
                if (chipLabel) {
                  const value = chipLabel.textContent || "";
                  handleDelete(value);
                }
              }
              return;
            }

            // Don't open menu if clicking on chips
            if (target.closest(".MuiChip-root")) {
              e.stopPropagation();
              return;
            }
          }}
        >
          {selected.length > 0
            ? (selected as string[]).map((value) => (
                <Chip
                  disabled={disabledDeleteValue === value}
                  key={value}
                  label={noFormatting ? value : formatColumnCaption(value)}
                  size="small"
                  sx={{
                    backgroundColor: "action.selected",
                  }}
                  onDelete={
                    disabledDeleteValue === value
                      ? undefined
                      : (e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDelete(value);
                        }
                  }
                  deleteIcon={
                    disabledDeleteValue === value ? undefined : (
                      <IconButton
                        size="small"
                        onMouseDown={(
                          e: React.MouseEvent<HTMLButtonElement>
                        ) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDelete(value);
                        }}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDelete(value);
                        }}
                        sx={{
                          padding: 0,
                          margin: 0,
                          "&:hover": { backgroundColor: "transparent" },
                          "& svg": {
                            fontSize: "18px",
                          },
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    )
                  }
                />
              ))
            : placeholder ? (
                // Match SearchableDropdown's grey placeholder (text.secondary).
                <Box sx={{ color: "text.secondary" }}>{placeholder}</Box>
              ) : (
                ""
              )}
        </Box>
      )}
      MenuProps={{
        // Ensure click-away detection works properly
        disableAutoFocusItem: true,
      }}
    >
      <ListSubheader sx={{ lineHeight: "40px" }}>
        {searchable ? (
          <TextField
            autoFocus
            placeholder="Type to search..."
            fullWidth
            variant="standard"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Escape") {
                // Prevents autoselecting item while typing (default Select behaviour)
                e.stopPropagation();
              }
            }}
          />
        ) : null}
      </ListSubheader>
      {displayedOptions.map((o, i) => (
        <MenuItem key={i} value={o}>
          {noFormatting ? o : formatColumnCaption(o)}
        </MenuItem>
      ))}
    </Select>
  );
}
